/**
 * The keyboard legend.
 *
 * Hidden below `lg` — the bindings still work if a keyboard is attached, but a
 * phone has no room to advertise them, and pointer-based media queries lie
 * often enough on hybrid devices that breakpoint is the honest signal here.
 */

import { memo } from "react";

import { cn } from "@/lib/utils";

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-grid h-5 min-w-5 place-items-center rounded border border-border bg-background px-1 font-sans text-[11px] font-medium text-foreground/80 shadow-[0_1px_0_var(--border)]">
      {children}
    </kbd>
  );
}

const BINDINGS: [React.ReactNode, string][] = [
  [
    <>
      <Key>↑</Key>
      <Key>↓</Key>
    </>,
    "Previous / next question",
  ],
  [
    <>
      <Key>←</Key>
      <Key>→</Key>
    </>,
    "Move the slider",
  ],
  [<Key>+</Key>, "Very important"],
  [<Key>−</Key>, "Not very important"],
  [<Key>*</Key>, "Normal importance"],
];

export const KeyboardHints = memo(function KeyboardHints({
  className,
}: {
  className?: string;
}) {
  return (
    <div
      className={cn(
        "hidden rounded-xl border border-border/60 bg-card/40 p-3 backdrop-blur-sm lg:block",
        className,
      )}
    >
      <div className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        Keyboard
      </div>
      <dl className="space-y-1.5">
        {BINDINGS.map(([keys, label]) => (
          <div key={label} className="flex items-center gap-2">
            <dt className="flex shrink-0 gap-1">{keys}</dt>
            <dd className="text-[11.5px] leading-tight text-muted-foreground">{label}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
});
